import React, { useEffect, useState } from "react";
import {
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableRow,
  Box,
  Paper,
  TablePagination,
  Typography,
  Grid2,
  Divider,
} from "@mui/material";
import AlertMessage from "../common/AlertMessage";
import { getStorageValue } from "../../utils/helper";
import "../../styles/ListingPageFilter.css";
import NoDataFound from "../common/NoDataFound";
import NotificationsListAPI from "../../utils/services/notifications.api";
import { ReactComponent as ReadNotification } from "../../assets/logos/read-notification.svg";
import { ReactComponent as UnReadNotification } from "../../assets/logos/unread-notification.svg";
import { useNavigate } from "react-router";
import PackageListings from "../../utils/services/PackageListing.api";
import dayjs from "dayjs";
import { onMessage } from "firebase/messaging";
import { messaging } from "../../notifications/firebase";
import Loaders from "../common/Loaders";

const NotificationsList = ({ isShowNotifications }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotificationList] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(10);
  const [totalData, setTotalData] = useState(0);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const userId = getStorageValue("userId");
  const { fetchNotificationsList, updateNotificationStatus, checkUnreadNotifications } =
    NotificationsListAPI;
  const [currentPage, setCurrentPage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  let isToday = true;
  let isYesterday = true;
  let isOlder = true;

  const getTableData = async () => {
    setIsLoading(true);
    const response = await fetchNotificationsList(
      userId,
      pageNumber + 1,
      pageCount
    );
    if (response.status !== "success") {
      setIsLoading(false);
      setAlertSeverity(response.status);
      setAlertMessage(
        response?.error?.response?.notifications?.message
          ? response?.error?.response?.notifications?.message
          : response.message
      );
      setOpen(true);
    } else {
      setIsLoading(false);
      setNotificationList(response?.data?.notifications);
      setTotalData(response?.data?.count);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePageChange = (e, newPage) => {
    setCurrentPage(newPage + 1);
    setPageNumber(newPage);
  };

  const handleRowsChange = (e) => {
    setPageCount(e.target.value);
    setCurrentPage(1);
    setPageNumber(0);
  };

  const isYesterdayDate = (notification) => {
    let currentDate = new Date();
    currentDate?.setDate(currentDate?.getDate() - 1);
    return (
      new Date(notification?.timeStamp).toLocaleDateString() ===
      currentDate.toLocaleDateString()
    );
  };

  useEffect(() => {
    getTableData();
    // eslint-disable-next-line
  }, [userId, currentPage, pageCount]);

  const handleVisibilityChange = async () => {
    if (document.visibilityState === 'visible') {
      const checkDate = new Date()?.toISOString();
        const response = await checkUnreadNotifications(userId, checkDate);
        if(response?.data?.message) {
          localStorage.setItem("isReload", false);
          localStorage.setItem("notifications", false);
          getTableData();
        }
    } 
  };

  useEffect(() => {
    onMessage(messaging, () => {
      getTableData();
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line
  }, [isShowNotifications])

  const handlePageNavigation = async (notification) => {
    const response = await PackageListings.getPackageDetailsbyId(
      notification?.jobId
    );
    if (notification?.isRead) {
      if (response?.status === "success") {
        navigate(`/packagedetails/${notification?.jobId}`,{
          state: {
            backURL: `/notifications`,
          },
        });
      } else {
        setAlertSeverity(response.status);
        setAlertMessage(
          response?.error?.data?.message
            ? response?.error?.data?.message
            : response.message
        );
        setOpen(true);
      }
    } else if (!notification?.isRead) {
      const payload = {
        notificationId: notification?.notificationId,
        userId: userId,
      };
      await updateNotificationStatus(payload);
      if (response?.status === "success") {
        navigate(`/packagedetails/${notification?.jobId}`,{
          state: {
            backURL: `/notifications`,
          },
        });
      } else {
        setAlertSeverity(response.status);
        setAlertMessage(
          response?.error?.data?.message
            ? response?.error?.data?.message
            : response.message
        );
        setOpen(true);
      }
    }
  };

  const gridLayout = (notification, value) => {
    if (value === 1) {
      isToday = false;
    } else if (value === 2) {
      isYesterday = false;
    } else if (value === 3) {
      isOlder = false;
    }
    return (
      <>
        <Grid2
          container
          spacing={2}
          sx={{
            padding: "10px",
            fontSize: "14px",
            fontWeight: 500,
            backgroundColor: !notification?.isRead ? "#E9F2FF" : "",
            color: notification?.isRead ? "#484C52" : "#000000",
            cursor: "pointer",
          }}
          onClick={() => handlePageNavigation(notification)}
        >
          <Grid2
            size={{ xs: 6, md: 9 }}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Box>
              {notification?.isRead ? (
                <ReadNotification />
              ) : (
                <UnReadNotification />
              )}
            </Box>
            <span className="notification-icon">
              {notification?.notificationContent}
            </span>
          </Grid2>
          <Grid2 size={{ xs: 6, md: 3 }} sx={{ display: "flex" }}>
            <span className="timestamp-notification">
              {dayjs(notification?.timeStamp+'Z').format("DD MMM YYYY, hh:mm A")}
            </span>
          </Grid2>
        </Grid2>
        <Divider />
      </>
    );
  };

  return (
    <>
      {isLoading && (
        <Loaders />
      )}
      {!isLoading && <TableContainer component={Paper} sx={{ marginTop: "2rem" }}>
        <Table sx={{ minWidth: 650 }} aria-label="notifications table">
          <TableBody>
            <TableRow width="100%">
              <Box sx={{ padding: "16px" }}>
                {notifications?.map((notification) =>
                  new Date(notification?.timeStamp).toLocaleDateString() ===
                  new Date().toLocaleDateString() ? (
                    <>
                      {isToday && (
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          fontSize={16}
                          paddingBottom={2}
                        >
                          Today
                        </Typography>
                      )}
                      {gridLayout(notification, 1)}
                    </>
                  ) : (
                    <></>
                  )
                )}

                {notifications?.map((notification) =>
                  isYesterdayDate(notification) ? (
                    <>
                      {isYesterday && (
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          fontSize={16}
                          paddingTop={2}
                          paddingBottom={2}
                        >
                          Yesterday
                        </Typography>
                      )}
                      {isYesterday && <Divider />}
                      {gridLayout(notification, 2)}
                    </>
                  ) : (
                    <></>
                  )
                )}

                {notifications?.map((notification) =>
                  !isYesterdayDate(notification) &&
                  new Date(notification?.timeStamp).toLocaleDateString() !==
                    new Date().toLocaleDateString() ? (
                    <>
                      {isOlder && (
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          fontSize={16}
                          paddingTop={2}
                          paddingBottom={2}
                        >
                          Older
                        </Typography>
                      )}
                      {isOlder && <Divider />}
                      {gridLayout(notification, 3)}
                    </>
                  ) : (
                    <></>
                  )
                )}
              </Box>
            </TableRow>
            {!isLoading && notifications?.length === 0 && (
              <TableRow width="100%">
                <TableCell width="100%" colSpan={5} sx={{ padding: "10rem" }}>
                  <NoDataFound />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>}
      {!isLoading && <Box>
        <TablePagination
          component="div"
          count={totalData}
          page={pageNumber}
          onPageChange={handlePageChange}
          rowsPerPage={pageCount}
          onRowsPerPageChange={handleRowsChange}
        />
      </Box>}

      <AlertMessage
        open={open}
        handleClose={handleClose}
        message={alertMessage}
        severity={alertSeverity}
      />
    </>
  );
};

export default React.memo(NotificationsList);
